import { get, post, put, del } from '@/services/http';
import {
  InventoryTransferItemModel,
  InventoryTransferItemAPIInterface,
} from '@/models/inventory_transfer_item';

export interface InventoryTransferItemFilters {
  inventoryTransferId?: number;
  productVariantId?: number;
}

export class InventoryTransferItems {
  public items: InventoryTransferItemModel[] = [];
  public total: number = 0;
  public loading: boolean = false;
  public error: string | null = null;
  private createOperations: number = 0;
  private updateOperations: number = 0;
  private deleteOperations: number = 0;

  public async fetch(filters: InventoryTransferItemFilters = {}): Promise<void> {
    this.loading = true;
    this.error = null;

    try {
      const params: any = {};
      if (filters.inventoryTransferId) { params.inventory_transfer_id = filters.inventoryTransferId; }
      if (filters.productVariantId) { params.product_variant_id = filters.productVariantId; }

      const response: any = await get('/inventory-transfer-items', params);
      const itemsArray = response.items || response;
      this.items = itemsArray.map(
        (item: InventoryTransferItemAPIInterface) =>
          new InventoryTransferItemModel().mapData(item),
      );
      this.total = this.items.length;
    } catch (error: any) {
      this.error = error.message || 'Failed to fetch transfer items';
      this.items = [];
      this.total = 0;
    } finally {
      this.loading = false;
    }
  }

  public async getById(itemId: number): Promise<InventoryTransferItemModel | null> {
    try {
      const response: any = await get(`/inventory-transfer-items/${itemId}`);
      return new InventoryTransferItemModel().mapData(response);
    } catch (error: any) {
      this.error = error.message || 'Failed to fetch transfer item details';
      return null;
    }
  }

  public async create(itemData: Partial<InventoryTransferItemAPIInterface>): Promise<InventoryTransferItemModel | null> {
    this.createOperations++;
    if (this.createOperations === 1) {
      this.loading = true;
    }
    this.error = null;

    try {
      const response: InventoryTransferItemAPIInterface = await post('/inventory-transfer-items', itemData);
      const newItem = new InventoryTransferItemModel().mapData(response);

      // Add to local collection
      this.items.push(newItem);
      this.total = this.items.length;

      return newItem;
    } catch (error: any) {
      this.error = error.message || 'Failed to create transfer item';
      return null;
    } finally {
      this.createOperations--;
      if (this.createOperations === 0) {
        this.loading = false;
      }
    }
  }

  public async update(itemId: number, itemData: Partial<InventoryTransferItemAPIInterface>): Promise<InventoryTransferItemModel | null> {
    this.updateOperations++;
    if (this.updateOperations === 1) {
      this.loading = true;
    }
    this.error = null;

    try {
      const response: InventoryTransferItemAPIInterface = await put(`/inventory-transfer-items/${itemId}`, itemData);
      const updatedItem = new InventoryTransferItemModel().mapData(response);

      // Update in local collection
      const index = this.items.findIndex((item) => item.id === itemId);
      if (index !== -1) {
        this.items[index] = updatedItem;
      }

      return updatedItem;
    } catch (error: any) {
      this.error = error.message || 'Failed to update transfer item';
      return null;
    } finally {
      this.updateOperations--;
      if (this.updateOperations === 0) {
        this.loading = false;
      }
    }
  }

  public async delete(itemId: number): Promise<boolean> {
    this.deleteOperations++;
    if (this.deleteOperations === 1) {
      this.loading = true;
    }
    this.error = null;

    try {
      await del(`/inventory-transfer-items/${itemId}`);

      // Remove from local collection
      const index = this.items.findIndex((item) => item.id === itemId);
      if (index !== -1) {
        this.items.splice(index, 1);
        this.total = this.items.length;
      }

      return true;
    } catch (error: any) {
      this.error = error.message || 'Failed to delete transfer item';
      return false;
    } finally {
      this.deleteOperations--;
      if (this.deleteOperations === 0) {
        this.loading = false;
      }
    }
  }

  public getByTransferId(transferId: number): InventoryTransferItemModel[] {
    return this.items.filter((item) => item.inventoryTransferId === transferId);
  }

  public getTotalQuantityForTransfer(transferId: number): number {
    return this.getByTransferId(transferId).reduce((sum, item) => sum + item.quantity, 0);
  }

  public mapData(itemsData: InventoryTransferItemAPIInterface[]): this {
    this.items = itemsData.map((item: InventoryTransferItemAPIInterface) =>
      new InventoryTransferItemModel().mapData(item),
    );
    this.total = this.items.length;
    return this;
  }

  public clearError(): void {
    this.error = null;
  }
}
